import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { huntCryptoRadar } from '../../services/CryptoRadarService';
import '../ProfitMonitor/ProfitMonitor.css';
import './CryptoRadar.css';

function formatPrice(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    if (number >= 1)
        return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    return number.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 8 });
}

function formatChange(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '--';
    const prefix = number > 0 ? '+' : '';
    return `${prefix}${number.toFixed(2)}%`;
}

function pairLabel(symbol) {
    return String(symbol || '').replace(/USDC$/, '/USDC').replace(/USDT$/, '/USDC');
}

function apiError(err) {
    return err?.response?.data || err?.message || 'Não foi possível varrer o radar agora.';
}

function CryptoRadar() {
    const navigate = useNavigate();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ type: '', text: '' });

    async function hunt(refresh = false) {
        setLoading(true);
        try {
            const data = await huntCryptoRadar(refresh);
            setResult(data);
            if (!data?.pick && !(data?.watch || []).length && !data?.topHunter)
                setNotification({
                    type: 'info',
                    text: 'Nenhuma moeda passou no filtro de alta neste momento. Tente de novo em alguns minutos.'
                });
        } catch (err) {
            setNotification({ type: 'error', text: apiError(err) });
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        hunt(false);
    }, []);

    function openMonitor(symbol, interval = '15m') {
        navigate(`/profit-monitor/${symbol}?interval=${interval || '15m'}`);
    }

    const pick = result?.pick;
    const runners = result?.runners || [];
    const watch = result?.watch || [];
    const topHunter = result?.topHunter;
    const hunters = result?.hunters || [];

    return (
        <>
            <Menu />
            <main className="content">
                <div className="profit-monitor crypto-radar">
                    <header className="profit-monitor-header">
                        <h1>Radar Cripto</h1>
                        <p>
                            Procura uma moeda no cruzamento do MACD — o mesmo gatilho do Monitor de Lucros —
                            em 15 minutos e/ou 1 hora. A opção Viés de 5 dias olha o padrão dos últimos cinco dias.
                            A opção Sinal técnico espera o COMPRAR e compra sozinha. A opção Só compra usa o mesmo viés de cinco dias e deixa a venda com você.
                            Nas três primeiras, o trade acompanha a alta: piso de 1,2%, topo livre (3%, 5% ou 7%) e venda na reversão.
                        </p>
                    </header>

                    <section className="profit-card crypto-radar-actions">
                        <div>
                            <strong>{result?.scanned ? `${result.scanned} pares analisados` : 'Aguardando varredura'}</strong>
                            <span>
                                {result?.updatedAt
                                    ? `Última varredura: ${new Date(result.updatedAt).toLocaleTimeString('pt-BR')}`
                                    : 'Mesma regra do Monitor: MACD acabou de cruzar, RSI abaixo de 65 e EMAs de alta, em 15m e 1h.'}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="profit-btn"
                            onClick={() => hunt(true)}
                            disabled={loading}
                        >
                            {loading ? 'Varrendo...' : 'Varrer de novo'}
                        </button>
                    </section>

                    {loading && !pick && !topHunter && (
                        <div className="profit-card profit-loading">
                            Varrendo até 100 pares USDC da Binance com a mesma regra de compra do Monitor de Lucros...
                        </div>
                    )}

                    {!loading && !pick && watch.length === 0 && !topHunter && (
                        <div className="profit-card">
                            <h2>Mercado sem sinal de compra agora</h2>
                            <p className="crypto-radar-watch-hint">
                                Não é bug: as 100 de maior volume em USDC estão com MACD e médias viradas para baixo.
                                O Radar só destaca COMPRAR (MACD acabou de cruzar, RSI abaixo de 65 e EMA 20 acima da EMA 50).
                                Quando a alta voltar, a lista aparece de novo. As opções Viés de 5 dias e Sinal técnico também esperam esse sinal.
                            </p>
                        </div>
                    )}

                    {topHunter && (
                        <section className="profit-card crypto-radar-pick crypto-radar-hunter">
                            <p className="crypto-radar-kicker">Viés de 5 dias</p>
                            <div className="crypto-radar-pick-head">
                                <div>
                                    <h2>{pairLabel(topHunter.symbol)}</h2>
                                    <p>{topHunter.name}</p>
                                </div>
                                <div className={`crypto-radar-chance chance-${topHunter.dailyChance >= 70 ? 'high' : topHunter.dailyChance >= 55 ? 'mid' : 'low'}`}>
                                    <strong>{topHunter.dailyChance}%</strong>
                                    <span>viés de alta no dia</span>
                                </div>
                            </div>
                            <p className="crypto-radar-summary">
                                {topHunter.dailyHunt?.summary
                                    || 'Padrão dos últimos 5 dias com viés de alta hoje. A opção Viés de 5 dias acompanha e espera o sinal técnico.'}
                            </p>
                            <div className="crypto-radar-metrics">
                                <div>
                                    <span>Dias verdes</span>
                                    <strong>{topHunter.dailyHunt?.greens ?? '--'}/5</strong>
                                </div>
                                <div>
                                    <span>5 dias</span>
                                    <strong className={Number(topHunter.dailyHunt?.fiveDay) >= 0 ? 'up' : 'down'}>
                                        {formatChange(topHunter.dailyHunt?.fiveDay)}
                                    </strong>
                                </div>
                                <div>
                                    <span>Hoje vs ontem</span>
                                    <strong className={Number(topHunter.dailyHunt?.todayVsYday) >= 0 ? 'up' : 'down'}>
                                        {formatChange(topHunter.dailyHunt?.todayVsYday)}
                                    </strong>
                                </div>
                                <div>
                                    <span>Cruzamento MACD</span>
                                    <strong>{topHunter.actionable ? 'sim' : 'ainda não'}</strong>
                                </div>
                            </div>
                            <ul className="profit-reasons">
                                {(topHunter.dailyHunt?.reasons || []).map(reason => (
                                    <li key={reason}>{reason}</li>
                                ))}
                            </ul>
                            <div className="crypto-radar-buttons">
                                <button
                                    type="button"
                                    className="profit-btn"
                                    onClick={() => openMonitor(topHunter.symbol, topHunter.actionable ? topHunter.interval : '1h')}
                                >
                                    Ver análise completa
                                </button>
                            </div>
                        </section>
                    )}

                    {hunters.length > 1 && (
                        <section className="profit-card">
                            <h2>Outras com viés nos 5 dias</h2>
                            <div className="crypto-radar-list">
                                {hunters.filter(row => row.symbol !== topHunter?.symbol).slice(0, 4).map(row => (
                                    <button
                                        type="button"
                                        key={`hunt-${row.symbol}`}
                                        className="crypto-radar-row"
                                        onClick={() => openMonitor(row.symbol, row.actionable ? row.interval : '1h')}
                                    >
                                        <div>
                                            <strong>{pairLabel(row.symbol)}</strong>
                                            <span>{row.dailyHunt?.greens ?? '--'}/5 dias verdes</span>
                                        </div>
                                        <div>
                                            <em>{row.dailyChance}%</em>
                                            <span className={Number(row.dailyHunt?.fiveDay) >= 0 ? 'up' : 'down'}>
                                                {formatChange(row.dailyHunt?.fiveDay)}
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    {pick && (
                        <section className="profit-card crypto-radar-pick">
                            <p className="crypto-radar-kicker">Melhor sinal agora</p>
                            <div className="crypto-radar-pick-head">
                                <div>
                                    <h2>{pairLabel(pick.symbol)}</h2>
                                    <p>{pick.name}</p>
                                </div>
                                <div className={`crypto-radar-chance chance-${pick.chance >= 70 ? 'high' : pick.chance >= 55 ? 'mid' : 'low'}`}>
                                    <strong>{pick.chance}%</strong>
                                    <span>chance técnica de alta</span>
                                </div>
                            </div>
                            <p className="crypto-radar-summary">{pick.summary}</p>
                            <div className="crypto-radar-metrics">
                                <div>
                                    <span>Horizonte</span>
                                    <strong>{pick.horizonLabel}</strong>
                                </div>
                                <div>
                                    <span>Preço</span>
                                    <strong>{formatPrice(pick.price)}</strong>
                                </div>
                                <div>
                                    <span>24h</span>
                                    <strong className={pick.changePerc >= 0 ? 'up' : 'down'}>{formatChange(pick.changePerc)}</strong>
                                </div>
                                <div>
                                    <span>Sinal 15m / 1h</span>
                                    <strong>{pick.nearReady ? 'COMPRAR' : 'esperar'} / {pick.hoursReady ? 'COMPRAR' : 'esperar'}</strong>
                                </div>
                            </div>
                            <ul className="profit-reasons">
                                {(pick.reasons || []).map(reason => (
                                    <li key={reason}>{reason}</li>
                                ))}
                            </ul>
                            <div className="crypto-radar-buttons">
                                <button
                                    type="button"
                                    className="profit-btn"
                                    onClick={() => openMonitor(pick.symbol, pick.interval)}
                                >
                                    Ver análise completa
                                </button>
                                <button
                                    type="button"
                                    className="profit-btn-secondary"
                                    onClick={() => navigate('/profit-monitor')}
                                >
                                    Abrir Monitor de Lucros
                                </button>
                            </div>
                        </section>
                    )}

                    {runners.length > 0 && (
                        <section className="profit-card">
                            <h2>Outras com potencial</h2>
                            <div className="crypto-radar-list">
                                {runners.map(row => (
                                    <button
                                        type="button"
                                        key={row.symbol}
                                        className="crypto-radar-row"
                                        onClick={() => openMonitor(row.symbol, row.interval)}
                                    >
                                        <div>
                                            <strong>{pairLabel(row.symbol)}</strong>
                                            <span>{row.horizonLabel}</span>
                                        </div>
                                        <div>
                                            <em>{row.chance}%</em>
                                            <span className={row.changePerc >= 0 ? 'up' : 'down'}>{formatChange(row.changePerc)}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    {watch.length > 0 && (
                        <section className="profit-card">
                            <h2>Acima de 60% — só para ver</h2>
                            <p className="crypto-radar-watch-hint">
                                Não passaram no sinal COMPRAR agora. A opção Sinal técnico olha essas e só compra quando o técnico ficar verde.
                            </p>
                            <div className="crypto-radar-list">
                                {watch.map(row => (
                                    <button
                                        type="button"
                                        key={row.symbol}
                                        className="crypto-radar-row watch"
                                        onClick={() => openMonitor(row.symbol, row.interval)}
                                    >
                                        <div>
                                            <strong>{pairLabel(row.symbol)}</strong>
                                            <span>{row.watchReason || row.horizonLabel}</span>
                                        </div>
                                        <div>
                                            <em>{row.chance}%</em>
                                            <span className="watch-tag">só olhar</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    <p className="crypto-radar-note">
                        A opção Viés de 5 dias usa o padrão dos últimos 5 dias. A opção Sinal técnico escolhe pelo sinal e espera o COMPRAR.
                        A opção Só compra segue o viés de cinco dias e deixa a venda com você.
                        As duas primeiras só compram no cruzamento do MACD. Moedas com chance técnica acima de 60% que ainda não cruzaram aparecem em “só para ver”.
                    </p>
                </div>
            </main>
            <Footer />
            {notification.text && (
                <Toast
                    type={notification.type}
                    text={notification.text}
                    onClose={() => setNotification({ type: '', text: '' })}
                />
            )}
        </>
    );
}

export default CryptoRadar;
